/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useMemo} from 'react';
import Divider from '@material-ui/core/Divider';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import Hidden from '@ion/dls/src/layout/Hidden';
import {dlsAlt, dlsColor, dlsZIndex} from '@ion/dls/src/theme/theme';
import {LabelTypography} from '@ion/dls/src/typography/LabelTypography';
import {TrackingFilters} from '../types/types';
import {resolveFilter} from './FilterHelpers';
import FilterSelectedOptions from './FilterSelectedOptions';
import {FilterState} from './useFilterState';
import {getFilterSelection, getUniqueSelectedOptions} from './utils';

/**
 * Defines the prop definitions for SelectedFilters ...
 *
 * filters          : Filters for which the selections are to be
 *                    determined.
 * onDelete         : Function invoked when a selected filter is
 *                    removed/deleted.
 * clearAll         : When true, all filter selections are cleared.
 * numberChipsLimit : Limits the number of filters to be shown
 *                    directly. Filters above this limit are shown
 *                    as more options.
 */
export type SelectedFiltersType = {
  filters: FilterState;
  onDelete: (id, value, selected) => void;
  clearAll: () => void;
  numberChipsLimit?: number;
  isTopSheetOpen?: boolean;
  shouldDisplayDateChip?: boolean;
  shouldDisplayFindNearbyChip?: boolean;
  shouldDisplayBoundaryChip?: boolean;
  boundaryFilterLabel?: string | undefined;
  handleClearDateChip?: () => void;
  handleClearBoundaryChip?: Function;
  handleClearFindNearbyChip?: Function;
};

// defines the styles used by SelectedFilters ...
export const useStyles = makeStyles(() => ({
  selectedFiltersWrapper : {
    display         : 'flex',
    position        : 'relative',
    flexDirection   : 'row',
    width           : '100%',
    minHeight       : '40px',
    marginTop       : ({isTopSheetOpen}: {isTopSheetOpen: boolean}) => isTopSheetOpen ? '0px' : '60px',
    padding         : '4px 0',
    backgroundColor : dlsColor('grey6'),
    zIndex          : dlsZIndex('trackingFilterSelectedFiltersWrapper'),
    '& #moreFilter' : {
      backgroundColor : 'transparent'
    },
    '& .MuiChip-root' : {
      margin : '2px'
    }
  },
  title : {
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'center',
    margin         : '0 0 0 8px'
  },
  mobileSelectedFiltersWrapper : {
    display           : 'block',
    width             : '100%',
    height            : '70px',
    padding           : '15px 0',
    overflowY         : 'auto',
    '& #chipsWrapper' : {
      display           : 'flex',
      padding           : '20px 10px',
      flexFlow          : 'wrap',
      alignItems        : 'center',
      justifyContent    : 'flex-start',
      '& .MuiChip-root' : {
        margin : '5px'
      }
    }
  },
  mobileSelectedFiltersHeader : {
    padding       : '10px 10px 0',
    marginLeft    : '5px',
    display       : 'block',
    ...dlsAlt('components', 'dropdown'),
    color         : dlsColor('grey3'),
    fontWeight    : 'bold !important',
    textTransform : 'uppercase'
  }
}));

// helps to get all filter selections count ...
export function getSelectedFiltersCount(filters) {
  let selections = [];
  for (const x in filters) {
    selections = [...selections, ...filters[x]?.selected];
  }
  return selections.length;
};

// helps to get all filter selections ...
export const getSelectedFilters = (filters: TrackingFilters) => {
  let selectedFilters = [];
  for (const [id, value] of Object.entries(filters)) {
    const item = value?.selected;
    selectedFilters = selectedFilters.concat(item?.map(selected => ({
      selections : [...item],
      selected,
      id
    })) || []);
  }
  return selectedFilters;
};

/**
 * The SelectedFilters component manages rendering all the filter
 * selections collectively with an option to remove the selections
 * individually or collectively.
 *
 * ```typescript
 *
 * import {SelectedFilters} from '@ion/dls/src/filters/SelectedFilters';
 *
 * <SelectedFilters filters={filters}
 *   onDelete={handleDelete}
 *   clearAll={clearAll}
 *   numberChipsLimit={10} />
 *
 * ```
 */
export const SelectedFilters = ({
  filters,
  onDelete,
  clearAll,
  numberChipsLimit = 5,
  shouldDisplayDateChip,
  shouldDisplayBoundaryChip,
  shouldDisplayFindNearbyChip,
  handleClearDateChip,
  boundaryFilterLabel,
  handleClearBoundaryChip,
  handleClearFindNearbyChip,
  isTopSheetOpen = false
}: SelectedFiltersType) => {
  const classes                                = useStyles({isTopSheetOpen});
  const selectedFilters                        = useMemo(() => getSelectedFilters(filters), [filters]);
  const {selectedCount: selectedFiltersCount}  = useMemo(() => getFilterSelection(selectedFilters), [selectedFilters]);

  const totalSelectedFilters = (
    selectedFiltersCount +
  (shouldDisplayDateChip ? 1 : 0) +
  (shouldDisplayBoundaryChip ? 1 : 0) +
  (shouldDisplayFindNearbyChip ? 1 : 0)
  );

  const handleDelete = useCallback(function({selected, selections, id}) {
    const {value} = resolveFilter(selected);
    const updates = selections?.filter(selection =>
      resolveFilter(selection)?.value !== value);
    onDelete?.(id, updates, selected);
  }, [onDelete]);

  const shouldDisplay = (
    selectedFiltersCount > 0 ||
    shouldDisplayDateChip ||
    shouldDisplayBoundaryChip ||
    shouldDisplayFindNearbyChip
  );

  if (shouldDisplay) {
    return (
      <>
        <Hidden show='desktop'>
          <div className={classes.selectedFiltersWrapper}>
            <div className={classes.title}>
              <LabelTypography>
                {`${translate('nav:filters.selectedFilters', {display: totalSelectedFilters})} |`}
              </LabelTypography>
            </div>
            <FilterSelectedOptions selectedOptions={selectedFilters}
              selectedOptionsCount={selectedFiltersCount}
              shouldDisplayDateChip={shouldDisplayDateChip}
              shouldDisplayFindNearbyChip={shouldDisplayFindNearbyChip}
              shouldDisplayBoundaryChip={shouldDisplayBoundaryChip}
              handleClearDateChip={handleClearDateChip}
              boundaryFilterLabel={boundaryFilterLabel}
              handleClearBoundaryChip={handleClearBoundaryChip}
              handleClearFindNearbyChip={handleClearFindNearbyChip}
              numberChipsLimit={numberChipsLimit}
              clearAll={clearAll}
              onChipDelete={handleDelete} />
          </div>
        </Hidden>
        <Hidden show='mobile'>
          <>
            <div className={classes.mobileSelectedFiltersWrapper}>
              <span className={classes.mobileSelectedFiltersHeader}>
                {`${translate('nav:filters.selectedFilters', {display: selectedFiltersCount})}`}
              </span>
              <FilterSelectedOptions selectedOptions={selectedFilters}
                selectedOptionsCount={selectedFiltersCount}
                shouldDisplayDateChip={shouldDisplayDateChip}
                shouldDisplayBoundaryChip={shouldDisplayBoundaryChip}
                shouldDisplayFindNearbyChip={shouldDisplayFindNearbyChip}
                handleClearDateChip={handleClearDateChip}
                boundaryFilterLabel={boundaryFilterLabel}
                handleClearBoundaryChip={handleClearBoundaryChip}
                handleClearFindNearbyChip={handleClearFindNearbyChip}
                numberChipsLimit={numberChipsLimit}
                onChipDelete={handleDelete} />
            </div>
            <Divider />
          </>
        </Hidden>
      </>

    );
  } else {
    return null;
  }
};

export default SelectedFilters;
